/**
 * QAGatePanel — pre-finalise deliverable-QA reconciliation gate. Runs the
 * server-side check that every headline number reconciles across the
 * deliverable set and that AI artefacts are genuine. Errors block finalisation;
 * warnings are advisory.
 */
import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, CheckCircle2, AlertTriangle, XCircle, Info, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUS_META = {
  pass: { cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Ready to finalise — all numbers reconcile" },
  warn: { cls: "text-amber-800 bg-amber-50 border-amber-200", label: "Finalise allowed, with advisories" },
  fail: { cls: "text-red-700 bg-red-50 border-red-200", label: "Blocked — reconcile the errors before finalising" },
};

function rowIcon(c) {
  if (c.ok) return <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5" />;
  if (c.severity === "error") return <XCircle size={14} className="text-red-600 shrink-0 mt-0.5" />;
  if (c.severity === "warn") return <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />;
  return <Info size={14} className="text-zinc-400 shrink-0 mt-0.5" />;
}

export default function QAGatePanel({ runId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gwDefault, setGwDefault] = useState(null);
  const [valueBase, setValueBase] = useState("");
  const [savingVas, setSavingVas] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/benefits-gateway-default")
      .then((r) => setGwDefault(!!r.data.enforced_by_default))
      .catch(() => {});
  }, []);

  const toggleGwDefault = async () => {
    const next = !gwDefault;
    try {
      await api.put("/admin/settings/benefits-gateway-default", { enforced_by_default: next });
      setGwDefault(next);
      toast.success(next ? "New runs will enforce the benefits gateway by default"
                         : "New runs will NOT enforce the benefits gateway by default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update default");
    }
  };

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/qa-check`);
      setReport(data);
      if (data.status === "fail") toast.error("QA gate failed — numbers don't reconcile");
      else if (data.status === "warn") toast.warning("QA gate passed with advisories");
      else toast.success("QA gate passed — every number reconciles");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "QA check failed");
    } finally {
      setLoading(false);
    }
  }, [runId]);

  const sm = report ? STATUS_META[report.status] : null;

  const saveValueBase = async () => {
    setSavingVas(true);
    try {
      await api.put(`/admin/assessments/runs/${runId}/value-at-stake`, {
        annual_value_base_gbp: Number(valueBase),
        value_base_description: "Annual change-portfolio spend (captured at the QA gate).",
      });
      setValueBase("");
      toast.success("Value base captured — re-running QA check");
      await run();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save value base");
    } finally {
      setSavingVas(false);
    }
  };

  const toggleGateway = async () => {
    const next = !report?.benefits_gateway_enforced;
    try {
      await api.put(`/admin/assessments/runs/${runId}/benefits-gateway`, { enforced: next });
      toast.success(next ? "Benefits gateway ENFORCED — finalise blocked until a validated value base is captured"
                         : "Benefits gateway relaxed (advisory only)");
      await run();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update gateway");
    }
  };

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="qa-gate-panel">
      <div className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
        <ShieldCheck size={14} /> Pre-finalise QA gate
      </div>
      <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
        Auto-checks that every headline number reconciles across the deliverable set (value at stake ↔ business case,
        maturity, portfolio) and that AI artefacts are genuine. Errors block finalisation.
      </p>

      <button
        type="button"
        onClick={run}
        disabled={loading}
        className="w-full mt-3 bg-[#0B0B0C] hover:bg-[#26262c] disabled:opacity-60 text-white text-[13px] font-medium px-3 py-2 flex items-center justify-center gap-2 transition-colors"
        data-testid="qa-gate-run"
      >
        {loading ? <RefreshCw size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
        {loading ? "Checking…" : report ? "Re-run QA check" : "Run QA check"}
      </button>

      <a
        href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments/runs/${runId}/export/preflightqa`}
        className="w-full mt-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-800 text-[12px] font-medium px-3 py-2 flex items-center justify-center gap-2 transition-colors"
        data-testid="qa-gate-preflight-pdf"
      >
        <ShieldCheck size={13} /> Download pre-flight QA report (PDF)
      </a>

      {report && (
        <div className="mt-4 space-y-3" data-testid="qa-gate-report">
          <div className={`text-[12px] px-3 py-2 border ${sm.cls}`} data-testid="qa-gate-status">
            <span className="font-semibold uppercase tracking-wide">{report.status}</span> — {sm.label}
          </div>
          <label className="flex items-center justify-between gap-2 text-[12px] bg-zinc-50 border border-zinc-200 px-3 py-2" data-testid="benefits-gateway-toggle">
            <span className="text-zinc-700">
              <span className="font-medium">Plan→Deliver benefits gateway</span>
              <span className="block text-[11px] text-zinc-400">Block finalisation until a client-validated value base is captured.</span>
            </span>
            <input
              type="checkbox"
              checked={!!report.benefits_gateway_enforced}
              onChange={toggleGateway}
              className="h-4 w-4 accent-[#7737F6] shrink-0"
              data-testid="benefits-gateway-checkbox"
            />
          </label>
          {gwDefault !== null && (
            <label className="flex items-center justify-between gap-2 text-[11px] px-3 py-1.5" data-testid="benefits-gateway-default-toggle">
              <span className="text-zinc-500">Enforce the gateway by default on <strong>new</strong> runs</span>
              <input
                type="checkbox"
                checked={gwDefault}
                onChange={toggleGwDefault}
                className="h-3.5 w-3.5 accent-[#7737F6] shrink-0"
                data-testid="benefits-gateway-default-checkbox"
              />
            </label>
          )}
          {(() => {
            const gw = report.checks.find((c) => c.id === "benefits_gateway");
            if (!gw || gw.ok) return null;
            return (
              <div className="bg-amber-50 border border-amber-200 px-3 py-2.5" data-testid="value-base-quick-action">
                <div className="text-[12px] font-medium text-amber-900">Capture a value base to clear the gateway</div>
                <p className="text-[11px] text-amber-700 mt-0.5 leading-snug">Enter the client's annual change-portfolio spend (£). This populates the business case and clears the block.</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[12px] text-amber-900">£</span>
                  <input
                    type="number"
                    min="0"
                    value={valueBase}
                    onChange={(e) => setValueBase(e.target.value)}
                    placeholder="e.g. 8000000"
                    className="ek-input text-[13px] flex-1"
                    data-testid="value-base-input"
                  />
                  <button
                    type="button"
                    onClick={saveValueBase}
                    disabled={savingVas || !valueBase}
                    className="bg-[#7737F6] hover:bg-[#5f2bcc] disabled:opacity-50 text-white text-[12px] font-medium px-3 py-1.5 transition-colors"
                    data-testid="value-base-save"
                  >
                    {savingVas ? "Saving…" : "Save & re-check"}
                  </button>
                </div>
              </div>
            );
          })()}
          <ul className="space-y-1.5">
            {report.checks.map((c) => (
              <li key={c.id} className="flex items-start gap-2 text-[12px]" data-testid={`qa-check-${c.id}`}>
                {rowIcon(c)}
                <span className="text-zinc-700">
                  <span className={c.ok ? "" : c.severity === "error" ? "text-red-700 font-medium" : ""}>{c.label}</span>
                  <span className="block text-[11px] text-zinc-400">{c.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
